import React, { useState, useEffect, useCallback } from 'react';
import '../styles/UsersAdmin.css';
import { api } from '../api';

const ROLE_FILTERS = [
  { id: '',         label: 'All' },
  { id: 'ca',       label: 'CA' },
  { id: 'team',     label: 'CA Team' },
  { id: 'customer', label: 'Customer' },
];

function displayRole(user) {
  if (user.role === 'admin') return 'admin';
  if (user.role === 'ca')    return 'ca';
  if (user.parent_ca_email)  return 'team';
  return 'customer';
}

function RoleBadge({ user }) {
  const role = displayRole(user);
  const map = {
    admin:    { label: 'Admin',    cls: 'ua-role-admin'    },
    ca:       { label: 'CA',       cls: 'ua-role-ca'       },
    team:     { label: 'CA Team',  cls: 'ua-role-team'     },
    customer: { label: 'Customer', cls: 'ua-role-customer' },
  };
  const { label, cls } = map[role];
  return <span className={`ua-role-badge ${cls}`}>{label}</span>;
}

function splitName(fullName) {
  if (!fullName) return { first: '', last: '' };
  const parts = fullName.trim().split(/\s+/);
  const first = parts[0] || '';
  const last  = parts.length > 1 ? parts.slice(1).join(' ') : '';
  return { first, last };
}

function fmtDate(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function useDebounce(value, delay = 350) {
  const [dv, setDv] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDv(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return dv;
}

export default function UsersAdmin({ userRole }) {
  const isAdmin = userRole?.isAdmin || false;

  const [users,   setUsers]   = useState([]);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(1);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [search,  setSearch]  = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  const LIMIT = 50;
  const debouncedSearch = useDebounce(search);

  const fetchUsers = useCallback(async (pg, role, q) => {
    setLoading(true);
    setError('');
    try {
      const data = await api.getAllUsers({ page: pg, limit: LIMIT, role, search: q });
      setUsers(data.users || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    setPage(1);
    fetchUsers(1, roleFilter, debouncedSearch);
  }, [isAdmin, roleFilter, debouncedSearch, fetchUsers]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchUsers(newPage, roleFilter, debouncedSearch);
  };

  if (!userRole?.id) {
    return <div className="ua-unauthorized" style={{ opacity: 0.5 }}>Loading…</div>;
  }
  if (!isAdmin) {
    return <div className="ua-unauthorized">
      Admin access required.
      {userRole?.role && <span style={{ marginLeft: 8, opacity: 0.6, fontSize: '0.85em' }}>(your role: {userRole.role})</span>}
    </div>;
  }

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="ua-page">
      <div className="ua-page-header">
        <h1 className="ua-title">
          All Users
          {total > 0 && <span className="ua-count-badge">{total.toLocaleString()}</span>}
        </h1>
      </div>

      <div className="ua-toolbar">
        <div className="ua-search-wrap">
          <span className="ua-search-icon">⌕</span>
          <input
            className="ua-search-input"
            type="text"
            placeholder="Search name, email, phone, PAN, ICAI…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button className="ua-search-clear" onClick={() => setSearch('')}>×</button>
          )}
        </div>

        <div className="ua-filter-tabs">
          {ROLE_FILTERS.map(f => (
            <button
              key={f.id}
              className={`ua-filter-btn${roleFilter === f.id ? ' ua-filter-btn--active' : ''}`}
              onClick={() => setRoleFilter(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="ua-error">{error}</div>}

      <div className="ua-table-wrap">
        <table className="ua-table">
          <thead>
            <tr>
              <th>First Name</th>
              <th>Last Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Parent CA</th>
              <th>PAN</th>
              <th>ICAI ID</th>
              <th>Joined</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="ua-loading">Loading…</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={8} className="ua-empty">No users found.</td></tr>
            ) : users.map(u => {
              const { first, last } = splitName(u.full_name);
              return (
                <tr key={u.id}>
                  <td>{first || <span className="ua-dim">—</span>}</td>
                  <td>{last  || <span className="ua-dim">—</span>}</td>
                  <td>{u.email}</td>
                  <td><RoleBadge user={u} /></td>
                  <td>
                    {u.parent_ca_name ? (
                      <div className="ua-parent-ca">
                        <div className="ua-parent-ca-name">{u.parent_ca_name}</div>
                        <div className="ua-parent-ca-email">{u.parent_ca_email}</div>
                      </div>
                    ) : <span className="ua-dim">—</span>}
                  </td>
                  <td>{u.pan_number     ? <span className="ua-mono">{u.pan_number}</span>     : <span className="ua-dim">—</span>}</td>
                  <td>{u.icai_member_id ? <span className="ua-mono">{u.icai_member_id}</span> : <span className="ua-dim">—</span>}</td>
                  <td className="ua-dim">{fmtDate(u.created_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {totalPages > 1 && (
          <div className="ua-pagination">
            <span className="ua-page-info">
              Showing {((page - 1) * LIMIT) + 1}–{Math.min(page * LIMIT, total)} of {total.toLocaleString()}
            </span>
            <div className="ua-page-btns">
              <button className="ua-page-btn" disabled={page <= 1} onClick={() => handlePageChange(page - 1)}>← Prev</button>
              <button className="ua-page-btn" disabled={page >= totalPages} onClick={() => handlePageChange(page + 1)}>Next →</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
